import { Box, Grid, Link } from '@mui/material';
import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import BlockData from '../../../models/BlockData';
import BlockProps from '../../../models/BlockProps';
import Utils from '../../../utils/Utils';
import BlockCenteredImageData from './BlockCenteredImageData';
import LinkButton from '../../Buttons/LinkButton';
import BlockComponentButton from '../../../models/BlockComponentValues/BlockComponentButton';
import CloseButton from '../../Buttons/Close';
import EditableText from '../../Form/EditableText';
import CroppedImage from '../../CroppedImage';
import ImageSettingsModal from '../../Modals/ImageSettingsModal/ImageSettingsModal';
import BlockComponentImage from '../../../models/BlockComponentValues/BlockComponentImage';
import useGetBackground from '../../../hooks/UseGetBackground';
import BackGroundImageModal from '../../Modals/ImageModal/BackGroundImageModal';
import useParallaxScript from '../../../hooks/useParallaxScript';
import config from '../../../config';
import ImageEnlargeModal from '../../Images/ImageEnlargeModal';

export interface BlockCenteredImageProps extends BlockProps {
  blockData: BlockData[]
}

const BlockCenteredImage = ({
  data,
  isEditing,
  isTemplate,
  editHandler,
  blockData,
  showImageModal,
  onCloseImageModal,
}: BlockCenteredImageProps) => {
  const targetModalId = uuid();

  const [props, setProps] = useState<BlockCenteredImageData>(data.data as BlockCenteredImageData);

  useEffect(() => {
    setProps(data.data as BlockCenteredImageData);
  }, [data.data]);

  const elementUuid = uuid();
  useParallaxScript(props.backgroundImage?.settings?.parallaxBool, elementUuid, config.backgroundImage.parallaxEffect);

  return (
    <Box
      id={elementUuid}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        ...useGetBackground(
          props.backgroundImage?.solidColor,
          props.backgroundImage?.value,
          props.backgroundImage?.settings?.scale,
          props.backgroundImage?.settings?.scaleBool,
        ),
        backgroundPosition: 'center 0px',
      }}
    >
      {(isEditing && !isTemplate) && (
        <BackGroundImageModal
          backgroundImage={props.backgroundImage}
          isShowModal={showImageModal}
          onCloseModal={onCloseImageModal}
          handleOnConfirm={(url: BlockComponentImage) => {
            const tempBg = { ...props, backgroundImage: { ...url } };
            setProps(tempBg);
            editHandler(data.id, tempBg);
          }}
        />
      )}

      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{
          padding: 1.5,
          overflow: 'hidden',
          maxWidth: '80%',
          width: '80%',
        }}
        className="block-content-wrapper"
      >

        {props.title.value && (
          <Grid item sx={Utils.hoverBorder(isEditing)}>
            <CloseButton
              show={isEditing}
              updateHandler={() => {
                props.title.value = undefined;
                editHandler(data.id, props);
              }}
            />
            <EditableText
              text={props.title.value}
              textColor={props.title.settings?.textColor}
              isEditable={isEditing && !isTemplate}
              updateHandler={(text) => {
                props.title.value = text;
                editHandler(data.id, props);
              }}
            />
          </Grid>
        )}

        {props.image.value !== undefined && (
          <Grid item sx={[Utils.hoverBorder(isEditing), { marginTop: 2, width: '100%' }]}>
            <CloseButton
              show={isEditing}
              updateHandler={() => {
                props.image.value = undefined;
                editHandler(data.id, props);
              }}
            />
            <ImageSettingsModal
              isEditing={isEditing}
              image={props.image}
              disabled={isTemplate || !isEditing}
              blockData={blockData}
              onConfirm={(newImage: BlockComponentImage) => {
                props.image = newImage;
                editHandler(data.id, props);
              }}
            >
              {(!isEditing) ? (
                <Link
                  style={{ cursor: props?.image?.link?.linkData?.url ? 'pointer' : 'default' }}
                  target={props?.image?.link?.linkData?.openType}
                  href={props?.image?.link?.linkData?.url || undefined}
                  sx={{
                    width: '100%',
                    height: '100%',
                  }}
                  rel="noreferrer"
                >
                  <CroppedImage
                    image={props.image}
                    isEditing={isEditing}
                    settings={props.image.settings}
                    targetModalId={targetModalId}
                  />
                </Link>
              ) : (
                <CroppedImage
                  image={props.image}
                  isEditing={isEditing}
                  settings={props.image.settings}
                  targetModalId={targetModalId}
                />
              )}
            </ImageSettingsModal>
          </Grid>
        )}

        {props.text.value && (
          <Grid item sx={[Utils.hoverBorder(isEditing), { marginTop: 2 }]}>
            <CloseButton
              show={isEditing}
              updateHandler={() => {
                props.text.value = undefined;
                editHandler(data.id, props);
              }}
            />
            <EditableText
              text={props.text.value}
              textColor={props.text.settings?.textColor}
              isEditable={isEditing && !isTemplate}
              updateHandler={(text) => {
                props.text.value = text;
                editHandler(data.id, props);
              }}
            />
          </Grid>
        )}

        {props.button && (
          <Grid item sx={[Utils.hoverBorder(isEditing), { height: '100%', width: '100%', marginTop: 2 }]}>
            <CloseButton
              show={isEditing}
              updateHandler={() => {
                props.button = undefined;
                editHandler(data.id, props);
              }}
            />
            <LinkButton
              button={props.button}
              isEditable={isEditing && !isTemplate}
              isTemplate={isTemplate}
              updateHandler={(button?: BlockComponentButton) => {
                props.button = button;
                editHandler(data.id, props);
              }}
              blockData={blockData}
            />
          </Grid>
        )}

      </Grid>

      <ImageEnlargeModal
        images={[props.image]}
        targetModalId={targetModalId}
      />
    </Box>
  );
};

export default BlockCenteredImage;
